import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";

export default function TransactionNotes({
  transactionId,
  paymentRef,
  apiUrl,
  onClose,
  onNoteAdded,
}) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  const safeApiUrl = String(apiUrl || "").replace(/\/+$/, "");

  const fetchNotes = async () => {
    if (!transactionId || !safeApiUrl) {
      toast.error("Transaction invalide : ID manquant.");
      return;
    }

    try {
      setLoading(true);

      const res = await fetchWithAuth(
        `${safeApiUrl}/api/admin/transactions/${transactionId}/notes`,
      );

      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.message || "Impossible de charger les notes");
      }

      const data = await res.json();
      setNotes(data.notes || []);
    } catch (err) {
      console.error(err);
      setNotes([]);
      toast.error(err.message || "Erreur de chargement des notes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId, safeApiUrl]);

  const handleAddNote = async () => {
    if (!transactionId || !safeApiUrl) {
      toast.error("Transaction invalide : ID manquant.");
      return;
    }

    if (!newNote.trim()) return;

    try {
      setAdding(true);

      const res = await fetchWithAuth(
        `${safeApiUrl}/api/admin/transactions/${transactionId}/notes`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            content: newNote.trim(),
          }),
        },
      );

      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.message || "Erreur lors de l'ajout de la note");
      }

      setNewNote("");

      // Reload notes so numbering stays correct
      await fetchNotes();

      // Tell the parent to update the memo count
      if (typeof onNoteAdded === "function") {
        onNoteAdded();
      }

      toast.success("Note ajoutée !");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Erreur lors de l'ajout de la note");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-md relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 dark:hover:text-white"
        >
          ✖
        </button>

        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
          Notes de la transaction #{paymentRef || transactionId}
        </h3>

        <div className="max-h-72 overflow-y-auto mb-4 border rounded-lg p-3 dark:bg-gray-700 dark:border-gray-600">
          {loading ? (
            <p className="text-gray-500">Chargement...</p>
          ) : notes.length === 0 ? (
            <p className="text-gray-500">Aucune note.</p>
          ) : (
            notes.map((note, index) => (
              <div
                key={note.id}
                className="mb-3 border rounded-lg p-3 dark:border-gray-600"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-500 text-white font-bold text-sm">
                      {note.number ?? index + 1}
                    </span>

                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {note.user?.email || "Admin"}
                      </p>

                      {note.createdAt && (
                        <p className="text-xs text-gray-500">
                          {new Date(note.createdAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <p className="text-sm whitespace-pre-wrap text-gray-800 dark:text-gray-100">
                  {note.content}
                </p>
              </div>
            ))
          )}
        </div>

        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Rédigez une nouvelle note..."
          className="w-full h-24 resize-none rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white p-3 mb-4"
        />

        <button
          type="button"
          onClick={handleAddNote}
          disabled={adding || !newNote.trim()}
          className="w-full px-4 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white font-medium disabled:opacity-50"
        >
          {adding ? "Ajout..." : "Ajouter la note"}
        </button>
      </div>
    </div>
  );
}
